import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';
import { ReorderableBlockList } from '../../CommonComponents/ReorderableBlockList';
import { RouteComponentProps } from '@reach/router';
import React, { useState } from 'react';

import {
  IOrganizeStoryFoldersGraphQlRequest,
  IOrganizeStorySectionsGraphQlRequest,
  IStoriesFolder,
  IStoryListMessages
} from '../../../../types';
import { getFolderById } from '../../../utils';

import { styles } from './OrganizeFoldersModal.styles';

export interface IOrganizeFoldersModalProps extends RouteComponentProps {
  messages: IStoryListMessages;
  selectedFolderId: string;
  folderStructure: IStoriesFolder[];
  entityId: number;
  currentFolderName: string;
  publicationId?: string;
  isOrganizePublicationSections?: boolean;
  userId?: number;
  organizeStoryFoldersByOrder?(props: IOrganizeStoryFoldersGraphQlRequest): Promise<void>;
  organizeStorySectionsByOrder?(props: IOrganizeStorySectionsGraphQlRequest): Promise<void>;
  onClose?(): void;
}

export const OrganizeFoldersModal: React.FC<IOrganizeFoldersModalProps> = (props) => {
  const {
    messages,
    isOrganizePublicationSections = false,
    selectedFolderId,
    folderStructure,
    organizeStoryFoldersByOrder,
    organizeStorySectionsByOrder,
    publicationId,
    entityId,
    currentFolderName,
    userId
  } = props;

  const selectedFolderNode = getFolderById(folderStructure, selectedFolderId) as IStoriesFolder;
  const [childrenNodes, setChildrenNodes] = useState(selectedFolderNode?.children);
  const [isSavingOrganizedFolders, setIsSavingOrganizedFolders] = useState(false);
  const [areFoldersReordered, setAreFoldersReordered] = useState(false);

  const onSaveOrganisedFolders = () => {
    setIsSavingOrganizedFolders(true);
    const orderedFoldersIds = childrenNodes.map((childNode) => childNode.id);
    if (!isOrganizePublicationSections) {
      void organizeStoryFoldersByOrder({ orderedFoldersIds, entityId, selectedFolderId, currentFolderName }).then(
        () => {
          setIsSavingOrganizedFolders(false);
          props.onClose();
        }
      );
    } else {
      void organizeStorySectionsByOrder({
        orderedSectionsIds: orderedFoldersIds,
        entityId,
        currentSectionName: currentFolderName,
        publicationId,
        userId
      }).then(() => {
        setIsSavingOrganizedFolders(false);
        props.onClose();
      });
    }
  };

  const onFolderDragEnd = (sourceIndex: number, destinationIndex: number) => {
    const updatedChildNodes = [...childrenNodes];
    const reorderedNode = updatedChildNodes.splice(sourceIndex, 1)[0];
    updatedChildNodes.splice(destinationIndex, 0, reorderedNode);
    setChildrenNodes(updatedChildNodes);
    setAreFoldersReordered(true);
  };

  return (
    <div data-test="organize-folders-modal">
      <Dialog open={true} onClose={props.onClose} aria-labelledby="organize-folders-title">
        <DialogTitle id="organize-folders-title">{publicationId ? messages.organizeSections : messages.organizeFolders}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {publicationId ? messages.organizationSectionDescription : messages.organizeFolderDescription}
          </DialogContentText>
          <Box sx={styles.folderName}>
            <Typography fontWeight="medium">{selectedFolderNode.displayName}</Typography>
          </Box>
          <Box sx={styles.reorderableList}>
            <ReorderableBlockList dataTest="reorderable-folders-list" onDragEnd={onFolderDragEnd}>
              {(childrenNodes || []).map((childNode: IStoriesFolder) => (
                <ReorderableBlockList.Item
                  dataTest="reorderable-folders-list-item"
                  key={childNode.id}
                  id={childNode.id}
                  label={childNode.displayName}
                />
              ))}
            </ReorderableBlockList>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={styles.buttons}>
            <Button data-test="cancel-button" onClick={props.onClose} variant="outlined">
              {messages.cancel}
            </Button>
            <Button
              disabled={isSavingOrganizedFolders || childrenNodes?.length < 2 || !areFoldersReordered}
              variant="contained"
              data-test="save-button"
              onClick={onSaveOrganisedFolders}
            >
              {messages.save}
            </Button>
            {isSavingOrganizedFolders && <CircularProgress size={16} data-test="loading-animation" aria-label="Saving" />}
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
};
